import { useRouter } from 'next/router';
import React from 'react';

import { BANNER_TYPES } from '@sravni/react-advertisement';
import { Space, Typography } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import { PAGE_TYPE } from '@sravni/ugc/lib/organizations/constants';

import { FILTER_NAMES, FILTERS } from '../../constants/reviews';
import { getProductHeading } from '../../helpers/reviews';
import { useIsWlPage } from '../../hooks/useIsWlPage';
import { IReviewFilters } from '../../interfaces/reviews';
import { useBanners } from '../../store/hooks/advertising';
import { useMetadata } from '../../store/hooks/metadata';
import { useProductDetails } from '../../store/hooks/products';
import pageStyles from '../../styles/page.module.scss';
import { TReviewListQuery } from '../../types/router';
import { BackLink } from '../BackLink';
import { BorderedBanner } from '../BorderedBanner';
import { LegalDisclaimer } from '../LegalDisclaimer';
import { IReviewsListProps } from '../ReviewsList';
import { SideBlock } from '../SideBlock';

import { FilterRegion } from './components/FilterRegion';
import { FilterStatus } from './components/FilterStatus';
import { HeadingDescription } from './components/HeadingDescription';
import { MainSelect } from './components/MainSelect';
import { MobileFilters } from './components/MobileFilters';
import { OrganizationHeader } from './components/OrganizationHeader';
import { ProductsAndSqueezeBlock } from './components/ProductsAndSqueezeBlock';
import { Sort } from './components/Sort';
import styles from './reviews-list-header.module.scss';

export interface IListHeaderProps extends IReviewsListProps {
  typePage: PAGE_TYPE;
  filters: FILTERS;
  currentServerFilters: IReviewFilters;
  handleChange: (value, target?: string) => void;
}

export const ReviewsListHeader: React.FC<IListHeaderProps> = ({
  typePage,
  reviewTypes,
  filters,
  currentServerFilters,
  handleChange,
  backLink,
}) => {
  const router = useRouter() as TRouter<TReviewListQuery>;
  const {
    query: { organization, type, product, specificProduct },
    asPath,
  } = router;
  const [pathname] = asPath.split('?');
  const metadata = useMetadata(pathname);
  const isWl = useIsWlPage();
  const productDetails = useProductDetails(specificProduct);

  const isMobile = useIsMobile();
  const banners = useBanners();

  const headingPage = specificProduct ? getProductHeading(product, typePage, productDetails) : metadata?.heading;

  const productTag = currentServerFilters.tag.find(({ alias }) => alias === product)?.key;

  const showProductsBlock = (organization || product) && !specificProduct;

  const showMenu = organization && !product;

  return (
    <div>
      {isMobile && (
        <>
          <BackLink type={reviewTypes.listTypeUrl} customLink={backLink?.href} customLabel={backLink?.label} />
          <SideBlock namesType={reviewTypes.namesType} hasButton />
        </>
      )}
      {organization && (
        <OrganizationHeader
          headingPage={headingPage}
          currentServerFilters={currentServerFilters}
          showMenu={showMenu}
          isMobile={isMobile}
        />
      )}
      {!organization && (
        <div className={styles.wrapper}>
          <Typography.Heading level={product ? 3 : 2} className={'h-mb-24'} as={'h1'}>
            {headingPage}
          </Typography.Heading>
          {isMobile && <HeadingDescription reviewTypes={reviewTypes} isMobile={isMobile} />}
          <MainSelect
            isMobile={isMobile}
            type={type}
            currentServerFilters={currentServerFilters}
            reviewTypes={reviewTypes}
          />
          <LegalDisclaimer className={'h-mt-12'} />
        </div>
      )}
      {!isWl && (
        <BorderedBanner
          banner={banners?.tgbm7}
          lazy={true}
          type={BANNER_TYPES.TGBM_7}
          className={!isMobile && 'h-mb-24'}
        />
      )}
      <ProductsAndSqueezeBlock
        product={product ? { tag: productTag, alias: product } : null}
        serverFilters={currentServerFilters}
        typePage={typePage}
        organizationAlias={organization}
        type={type}
        isMobile={isMobile}
        showProductsBlock={showProductsBlock}
        filters={filters}
        handleChange={handleChange}
      />
      <div className={pageStyles.mainColumnInner}>
        <Space justify={isMobile ? 'end' : 'space-between'} className={isMobile ? 'h-mt-16 h-mb-16' : 'h-mb-24'}>
          {!isMobile && (
            <Sort
              filters={filters}
              currentServerFilters={currentServerFilters}
              typePage={typePage}
              handleChange={handleChange}
              isMobile={isMobile}
            />
          )}
          {isMobile ? (
            <MobileFilters
              filters={filters}
              currentServerFilters={currentServerFilters}
              typePage={typePage}
              handleChange={handleChange}
            />
          ) : (
            <Space justify={'end'} size={8}>
              <FilterStatus
                filters={filters}
                currentServerFilters={currentServerFilters}
                typePage={typePage}
                handleChange={handleChange}
              />
              <FilterRegion locationGarId={filters[FILTER_NAMES.LOCATION_GAR_ID]} handleChange={handleChange} />
            </Space>
          )}
        </Space>
      </div>
    </div>
  );
};
